import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Autocomplete,
} from '@mui/material';
import { Article as ArticleIcon } from '@mui/icons-material';
import { fetchGA4Metrics, fetchGA4Events, type GA4Metrics, type GA4EventsResponse } from '../../services/analytics.service';
import { usePagesList } from '../../hooks/usePagesList';

export default function Ga4AnalyticsPage() {
  const [pageId, setPageId] = useState<number | ''>('');
  const [tenantId, setTenantId] = useState<number | ''>('');
  const [daysBack, setDaysBack] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<GA4Metrics | null>(null);
  const [events, setEvents] = useState<GA4EventsResponse | null>(null);

  // Use the common prepopulated pages list
  const { pages, loading: pagesLoading, error: pagesError } = usePagesList();

  const load = async () => {
    if (pageId === '' || pageId === undefined) return;
    setLoading(true);
    setError(null);
    setMetrics(null);
    setEvents(null);
    try {
      const [metricsRes, eventsRes] = await Promise.all([
        fetchGA4Metrics({
          page_id: Number(pageId),
          tenant_id: tenantId === '' ? undefined : tenantId,
          days_back: daysBack,
        }),
        fetchGA4Events({
          page_id: Number(pageId),
          tenant_id: tenantId === '' ? undefined : tenantId,
          days_back: daysBack,
          limit: 20,
        }),
      ]);
      setMetrics(metricsRes.data);
      setEvents(eventsRes.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load GA4 data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5">GA4</Typography>
      <Typography variant="body2" color="text.secondary">
        Behavioral metrics and event data from Google Analytics 4.
      </Typography>

      {/* Controls */}
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Autocomplete
              options={pages}
              getOptionLabel={(option) => option.label}
              value={pages.find((p) => p.id === pageId) || null}
              onChange={(_, newValue) => setPageId(newValue?.id ?? '')}
              loading={pagesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Page"
                  size="small"
                  helperText={pagesError ? 'Failed to load pages' : ''}
                  error={!!pagesError}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <ArticleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2">{option.url}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
              sx={{ width: 300 }}
              size="small"
            />
            <TextField
              label="Tenant ID"
              type="number"
              size="small"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ width: 160 }}
            />
            <TextField
              label="Days Back"
              type="number"
              size="small"
              value={daysBack}
              onChange={(e) => setDaysBack(Math.max(1, Number(e.target.value) || 7))}
              sx={{ width: 140 }}
            />
            <Button variant="contained" onClick={load} disabled={loading || pageId === ''}>
              Load
            </Button>
            {loading && <CircularProgress size={20} />}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </CardContent>
      </Card>

      {/* Metrics */}
      {metrics && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Metrics</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {[
                { label: 'Bounce Rate', value: metrics.bounce_rate, unit: '%' },
                { label: 'Time on Page (sec)', value: metrics.time_on_page },
                { label: 'Scroll Depth', value: metrics.scroll_depth, unit: '%' },
                { label: 'Conversion Rate', value: metrics.conversion_rate, unit: '%' },
              ].map((m) => (
                <Grid item xs={12} sm={6} md={3} key={m.label}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">  {m.label}
                    </Typography>
                    <Typography variant="h6">
                      {(m.value ?? 0).toFixed(2)}{m.unit ?? ''}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Total Events</Typography>
                  <Typography variant="h6">{metrics.total_events ?? '-'}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Events */}
      {events && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Events</Typography>
            <Divider sx={{ mb: 2 }} />
            {!events.items || events.items.length === 0 ? (
              <Typography>No events found.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>Event Name</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Users</TableCell>
                      <TableCell align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.items.map((evt, idx) => (
                      <TableRow key={(evt as any).id ?? idx}>
                        <TableCell>{(evt as any).event_name ?? '-'}</TableCell>
                        <TableCell align="right">{(evt as any).event_count ?? '-'}</TableCell>
                        <TableCell align="right">{(evt as any).user_count ?? '-'}</TableCell>
                        <TableCell align="right">{((evt as any).event_value ?? 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
